import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Event } from '../types';
import { 
  LogOut, ArrowLeft, LayoutDashboard, QrCode, FileSpreadsheet,
  Users, ShieldAlert, Database, Briefcase, Menu, X, ChevronDown, 
  ImageIcon, MessageSquare, Settings, Sparkles, Calendar, ChevronRight,
  Shield, UserCheck, BookOpen, HelpCircle, Building2, Smartphone
} from 'lucide-react';
import NavigationBreadcrumbs from './NavigationBreadcrumbs';

export default function Layout({ hideSidebar = false }: { hideSidebar?: boolean }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState<Event | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mktmMenuOpen, setMktmMenuOpen] = useState(false);
  const [guestsMenuOpen, setGuestsMenuOpen] = useState(false);
  const [mobileMktmOpen, setMobileMktmOpen] = useState(true);
  const [mobileGuestsOpen, setMobileGuestsOpen] = useState(true);
  const [pendingPhonesCount, setPendingPhonesCount] = useState(0);

  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const mktmDropdownRef = useRef<HTMLDivElement>(null);
  const guestsDropdownRef = useRef<HTMLDivElement>(null);
  
  // Extract eventId from path
  const pathParts = location.pathname.split('/');
  const eventIdIndex = pathParts.indexOf('events');
  const eventId = eventIdIndex !== -1 && pathParts.length > eventIdIndex + 1 
    ? pathParts[eventIdIndex + 1] 
    : null;
  const effectiveEventId = eventId || localStorage.getItem('lenovo_last_event_id');

  useEffect(() => {
    const targetToFetch = eventId || effectiveEventId;
    if (eventId) {
      localStorage.setItem('lenovo_last_event_id', eventId);
    }
    if (targetToFetch) {
      getDoc(doc(db, 'events', targetToFetch)).then(docSnap => {
        if (docSnap.exists()) {
          setEvent({ id: docSnap.id, ...docSnap.data() } as Event);
        }
      });
    }

    if (effectiveEventId) {
      // Listen to pending phone validations count
      const q = query(
        collection(db, 'guests'),
        where('eventId', '==', effectiveEventId),
        where('phone_validation_status', '==', 'pending_validation')
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setPendingPhonesCount(snapshot.size);
      }, (err: any) => {
        if (!err?.message?.includes('Quota') && err?.code !== 'resource-exhausted') {
          console.warn('Could not query pending phone validations count', err);
        }
      });

      return () => unsubscribe();
    }
  }, [eventId, effectiveEventId]);

  // Close mobile menu and dropdown on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMegaMenuOpen(false);
    setMktmMenuOpen(false);
    setGuestsMenuOpen(false);
    if (location.pathname.includes('/guests/')) {
      setMobileGuestsOpen(true);
    }
    if (location.pathname.endsWith('/mktm') || location.pathname.includes('/staff')) {
      setMobileMktmOpen(true);
    }
  }, [location.pathname]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target as Node)) {
        setMegaMenuOpen(false);
      }
      if (mktmDropdownRef.current && !mktmDropdownRef.current.contains(e.target as Node)) {
        setMktmMenuOpen(false);
      }
      if (guestsDropdownRef.current && !guestsDropdownRef.current.contains(e.target as Node)) {
        setGuestsMenuOpen(false);
      }
    };
    if (megaMenuOpen || mktmMenuOpen || guestsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [megaMenuOpen, mktmMenuOpen, guestsMenuOpen]);

  const isAdmin = user?.role === 'admin';
  const canAccessMarketing = Boolean(user?.canAccessMarketingMaterials);
  const canAccessSms = Boolean(user?.canAccessSmsCampaigns);

  const NavItem = ({ to, icon: Icon, label, isActive }: { to: string, icon: any, label: string, isActive: boolean }) => (
    <Link 
      to={to}
      className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 flex-shrink-0 ${
        isActive 
          ? 'bg-white text-black font-bold border-[#E2231A]' 
          : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
      }`}
    >
      <Icon className="mr-2 h-4 w-4 flex-shrink-0" />
      {label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#F2F2F2] flex flex-col font-sans">
      {/* Top Navbar (Level 1) */}
      <header className="bg-white border-b border-gray-200 z-50 sticky top-0">
        <div className="w-full max-w-[1920px] 2xl:max-w-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="flex flex-col cursor-pointer" onClick={() => navigate('/')}>
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-black leading-none flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#E2231A] inline-block sm:hidden"></span>
                Marketing Matters
              </h1>
              <span className="text-[10px] font-semibold text-gray-500 tracking-wide uppercase">Enterprise Event Portal</span>
            </div>

            {event && (
              <div className="hidden sm:flex items-center gap-2 pl-4 border-l border-gray-200">
                <span className="text-xs font-semibold text-slate-700 max-w-[200px] lg:max-w-[320px] truncate" title={event.name || event.title || 'Eveniment'}>
                  {event.name || event.title || 'Eveniment'}
                </span>
                <span className="text-slate-300 text-xs select-none">|</span>
                <Link to="/" className="text-[11px] text-[#E2231A] hover:underline font-semibold flex items-center gap-0.5">
                  Schimbă
                </Link>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden md:flex flex-col items-end mr-2">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-bold text-black leading-none">{user?.name}</span>
                {user?.isSuperAdmin && (
                  <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-800 uppercase">SuperAdmin</span>
                )}
              </div>
              <span className="text-[10px] uppercase text-gray-500 font-medium">{user?.role}</span>
            </div>

            {(user?.role === 'admin' || user?.isSuperAdmin) && (
              <Link
                to={eventId ? `/events/${eventId}/help` : "/help"}
                title="Manual de Utilizare & Ghid Tehnic"
                className="hidden sm:flex items-center gap-1.5 text-slate-600 hover:text-[#E2231A] transition-colors px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-xs font-semibold border border-slate-200/60"
              >
                <BookOpen className="h-4 w-4 text-[#E2231A]" />
                <span className="hidden xl:inline">Manual Utilizare</span>
              </Link>
            )}

            <button 
              onClick={signOut} 
              title="Deconectare"
              className="hidden sm:flex items-center text-[#4A4A4A] hover:text-[#E2231A] transition-colors p-2 rounded-lg hover:bg-slate-100"
            >
              <LogOut className="h-5 w-5" />
            </button>

            {/* Mobile Menu Toggle Button */}
            <button 
              className="lg:hidden p-2 text-black hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Meniu Navigare"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6 text-[#E2231A]" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Slide-Over Menu / Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity" 
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Drawer Content */}
          <div className="relative ml-auto w-full max-w-xs sm:max-w-sm bg-white h-full shadow-2xl flex flex-col z-10 overflow-y-auto">
            {/* Drawer Header */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h2 className="font-bold text-slate-900 text-base">Navigare & Meniu</h2>
                <span className="text-[11px] text-slate-500 font-medium">{user?.name} ({user?.role})</span>
              </div>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User & Active Event Pill */}
            <div className="p-4 bg-slate-50/50 border-b border-gray-100 space-y-2">
              <Link 
                to="/" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-between w-full p-2.5 bg-white rounded-xl border border-slate-200 shadow-xs hover:border-[#E2231A] transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Calendar className="w-4 h-4 text-[#E2231A] flex-shrink-0" />
                  <div className="truncate">
                    <span className="text-xs font-bold text-slate-900 block truncate">
                      {event ? (event.name || event.title) : 'Vezi Toate Evenimentele'}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {event ? 'Eveniment Activ (Apasă pt. a schimba)' : 'Selectează un eveniment'}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </Link>
            </div>

            {/* Event Specific Navigation */}
            <div className="p-4 space-y-4 flex-1">
              {!hideSidebar && eventId ? (
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-2 px-2">
                    Meniu Eveniment
                  </span>
                  <div className="space-y-1">
                    {isAdmin ? (
                      <>
                        <Link 
                          to={`/events/${eventId}/dashboard`}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${location.pathname.endsWith('/dashboard') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50'}`}
                        >
                          <LayoutDashboard className="w-4 h-4" /> Dashboard
                        </Link>
                        
                        {/* Mobile MKTM Accordion */}
                        <div className="rounded-xl overflow-hidden border border-slate-200/80 bg-slate-50/50 my-0.5">
                          <button
                            type="button"
                            onClick={() => setMobileMktmOpen(prev => !prev)}
                            className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold transition-colors ${location.pathname.endsWith('/mktm') || location.pathname.includes('/staff') ? 'text-[#C41A12] bg-red-50/50' : 'text-slate-700 hover:bg-slate-100'}`}
                          >
                            <div className="flex items-center gap-3">
                              <FileSpreadsheet className="w-4 h-4 text-[#E2231A]" />
                              <span>MKTM</span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${mobileMktmOpen ? 'rotate-180 text-[#E2231A]' : ''}`} />
                          </button>

                          {mobileMktmOpen && (
                            <div className="pl-9 pr-2 py-1.5 space-y-1 bg-white border-t border-slate-100">
                              <Link 
                                to={`/events/${eventId}/mktm`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.endsWith('/mktm') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>MKTM (Toți Invitații)</span>
                                {location.pathname.endsWith('/mktm') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                              <Link 
                                to={`/events/${eventId}/guests/staff`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.includes('/staff') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>Staff</span>
                                {location.pathname.includes('/staff') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                            </div>
                          )}
                        </div>

                        {/* Mobile Grouped Invitați Accordion */}
                        <div className="rounded-xl overflow-hidden border border-slate-200/80 bg-slate-50/50 my-0.5">
                          <button
                            type="button"
                            onClick={() => setMobileGuestsOpen(prev => !prev)}
                            className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold transition-colors ${location.pathname.includes('/guests/') ? 'text-[#C41A12] bg-red-50/50' : 'text-slate-700 hover:bg-slate-100'}`}
                          >
                            <div className="flex items-center gap-3">
                              <Users className="w-4 h-4 text-[#E2231A]" />
                              <span>Invitați</span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${mobileGuestsOpen ? 'rotate-180 text-[#E2231A]' : ''}`} />
                          </button>

                          {mobileGuestsOpen && (
                            <div className="pl-9 pr-2 py-1.5 space-y-1 bg-white border-t border-slate-100">
                              <Link 
                                to={`/events/${eventId}/guests/lenovo-team`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.includes('/guests/lenovo-team') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>Lenovo Team</span>
                                {location.pathname.includes('/guests/lenovo-team') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                              <Link 
                                to={`/events/${eventId}/distributors`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.includes('/distributors') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>Distribuitori</span>
                                {location.pathname.includes('/distributors') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                              <Link 
                                to={`/events/${eventId}/guests/resellers`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.includes('/guests/resellers') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>Reselleri</span>
                                {location.pathname.includes('/guests/resellers') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                              <Link 
                                to={`/events/${eventId}/guests/end-users`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.includes('/guests/end-users') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>End Useri (Lenovo)</span>
                                {location.pathname.includes('/guests/end-users') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                              <Link 
                                to={`/events/${eventId}/guests/partner-end-users`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.includes('/guests/partner-end-users') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>End Useri (Reselleri)</span>
                                {location.pathname.includes('/guests/partner-end-users') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                              <Link 
                                to={`/events/${eventId}/guests/managers`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border-t border-slate-100 ${location.pathname.includes('/guests/managers') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <span>Invitați pe Manageri</span>
                                {location.pathname.includes('/guests/managers') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                            </div>
                          )}
                        </div>

                        <Link 
                          to={`/events/${eventId}/validations`}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${location.pathname.includes('/validations') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50'}`}
                        >
                          <div className="flex items-center gap-3">
                            <ShieldAlert className="w-4 h-4 text-amber-600" />
                            <span>Validări</span>
                          </div>
                          {pendingPhonesCount > 0 && (
                            <span className="px-1.5 py-0.2 bg-red-100 text-[#C41A12] text-[10px] font-bold rounded-full">
                              {pendingPhonesCount}
                            </span>
                          )}
                        </Link>

                        <Link 
                          to={`/events/${eventId}/scanner`}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${location.pathname.includes('/scanner') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50'}`}
                        >
                          <QrCode className="w-4 h-4" /> Check-in / Scanner
                        </Link>
                        {canAccessMarketing && (
                          <Link 
                            to={`/events/${eventId}/invitations`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${location.pathname.includes('/invitations') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50'}`}
                          >
                            <ImageIcon className="w-4 h-4" /> Materiale Marketing
                          </Link>
                        )}
                        {canAccessSms && (
                          <Link 
                            to={`/events/${eventId}/sms`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${location.pathname.includes('/sms') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50'}`}
                          >
                            <MessageSquare className="w-4 h-4" /> Campanii SMS
                          </Link>
                        )}
                      </>
                    ) : (
                      <Link 
                        to={`/events/${eventId}/partner`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${location.pathname.includes('/partner') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <Users className="w-4 h-4" /> Partner Portal
                      </Link>
                    )}
                  </div>
                </div>
              ) : effectiveEventId ? (
                <div className="p-3 bg-red-50/70 rounded-xl border border-red-100 mb-2">
                  <div className="text-[10px] font-bold text-[#C41A12] uppercase tracking-wider mb-1.5">
                    Eveniment Activ în Lucru
                  </div>
                  <Link
                    to={`/events/${effectiveEventId}/dashboard`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-between p-2 rounded-lg bg-white border border-red-200 text-xs font-bold text-slate-800 hover:text-[#C41A12] shadow-2xs transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ArrowLeft className="w-3.5 h-3.5 text-[#E2231A] shrink-0" />
                      <span className="truncate">{event?.name || 'Dashboard Eveniment'}</span>
                    </div>
                    <span className="text-[10px] text-[#E2231A] shrink-0 font-semibold">Deschide →</span>
                  </Link>
                </div>
              ) : null}

              {/* Global Admin Section (Clearly visible on phone) */}
              {isAdmin && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between px-2 mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Meniu Admin & Setări
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-600 uppercase">
                      Admin
                    </span>
                  </div>
                  <div className="space-y-1 bg-slate-50 p-2 rounded-xl border border-slate-200">
                    {user?.isSuperAdmin && (
                      <>
                        <Link 
                          to="/mktm-team" 
                          onClick={() => setIsMobileMenuOpen(false)} 
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/mktm-team' || location.pathname === '/mktm' ? 'bg-white shadow-xs text-black font-bold' : 'text-slate-700 hover:bg-white hover:text-black'}`}
                        >
                          <Users className="w-4 h-4 text-[#E2231A]" />
                          Echipa MKTM (Hostess & Admini)
                        </Link>
                        <Link 
                          to="/team" 
                          onClick={() => setIsMobileMenuOpen(false)} 
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/team' ? 'bg-white shadow-xs text-black font-bold' : 'text-slate-700 hover:bg-white hover:text-black'}`}
                        >
                          <Briefcase className="w-4 h-4 text-[#E2231A]" />
                          Echipa Lenovo
                        </Link>
                      </>
                    )}
                    {(user?.role === 'admin' || user?.isSuperAdmin) && (
                      <>
                        <div className="space-y-1">
                          <Link 
                            to="/clients" 
                            onClick={() => setIsMobileMenuOpen(false)} 
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/clients' ? 'bg-white shadow-xs text-black font-bold' : 'text-slate-700 hover:bg-white hover:text-black'}`}
                          >
                            <Database className="w-4 h-4 text-[#E2231A]" />
                            Baza Date Clienți
                          </Link>
                          {effectiveEventId && (
                            <div className="pl-6 space-y-1 border-l-2 border-slate-200 ml-4 py-1">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block px-2.5 py-0.5">
                                Validări
                              </span>
                              <Link 
                                to={`/events/${effectiveEventId}/validations`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.endsWith('/validations') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <div className="flex items-center gap-1.5">
                                  <ShieldAlert className="w-3 h-3 text-amber-600" />
                                  <span>Validări Înscrieri</span>
                                </div>
                                {location.pathname.endsWith('/validations') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                              </Link>
                              <Link 
                                to={`/events/${effectiveEventId}/validations/phones`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${location.pathname.includes('/validations/phones') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                              >
                                <div className="flex items-center gap-1.5">
                                  <Smartphone className="w-3 h-3 text-[#E2231A]" />
                                  <span>Validări Telefoane</span>
                                </div>
                                {pendingPhonesCount > 0 && (
                                  <span className="px-1.5 py-0.2 bg-red-100 text-[#C41A12] text-[10px] font-bold rounded-full">
                                    {pendingPhonesCount}
                                  </span>
                                )}
                              </Link>
                            </div>
                          )}
                        </div>
                        <div className="border-t border-slate-200/60 my-1.5 pt-1.5 space-y-1">
                          {user?.isSuperAdmin && (
                            <Link 
                              to={eventId ? `/events/${eventId}/settings` : "/settings"} 
                              onClick={() => setIsMobileMenuOpen(false)} 
                              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${location.pathname.includes('/settings') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-800 hover:bg-white'}`}
                            >
                              <Settings className="w-4 h-4 text-[#E2231A]" />
                              Setări Integrări (Mailgun & SMS)
                            </Link>
                          )}
                          <Link 
                            to={eventId ? `/events/${eventId}/help` : "/help"} 
                            onClick={() => setIsMobileMenuOpen(false)} 
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-bold transition-colors ${location.pathname.includes('/help') || location.pathname.includes('/manual') ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'text-slate-800 hover:bg-white'}`}
                          >
                            <BookOpen className="w-4 h-4 text-amber-600" />
                            Manual Utilizare & FAQ
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-gray-100 bg-slate-50">
              <button 
                onClick={signOut}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 text-[#C41A12] hover:bg-red-100 font-bold text-sm transition-colors"
              >
                <LogOut className="w-4 h-4" /> Deconectare
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-navbar (Level 2) - Desktop & Tablet horizontal bar */}
      {!hideSidebar && eventId && (
        <div className="hidden lg:block bg-[#F2F2F2] border-b border-gray-300 shadow-xs sticky top-16 z-40">
          <div className="w-full max-w-[1920px] 2xl:max-w-full mx-auto px-2 sm:px-6 lg:px-8 flex justify-between items-center min-w-0">
            {/* Horizontal Nav for all items - overflow visible so dropdowns are fully visible */}
            <div className="flex items-center flex-1 min-w-0 py-0.5 overflow-visible">
              {isAdmin && (
                <>
                  <NavItem to={`/events/${eventId}/dashboard`} icon={LayoutDashboard} label="Dashboard" isActive={location.pathname.endsWith('/dashboard')} />
                  
                  {/* MKTM Dropdown Menu (Desktop & Tablet) */}
                  <div className="relative flex-shrink-0 z-50" ref={mktmDropdownRef}>
                    <button 
                      type="button"
                      onClick={() => setMktmMenuOpen(prev => !prev)}
                      className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 cursor-pointer ${
                        mktmMenuOpen || location.pathname.endsWith('/mktm') || location.pathname.includes('/staff')
                          ? 'bg-white text-black font-bold border-[#E2231A]' 
                          : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                      }`}
                      aria-expanded={mktmMenuOpen}
                    >
                      <FileSpreadsheet className="mr-2 h-4 w-4 flex-shrink-0" />
                      MKTM
                      <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform ${mktmMenuOpen ? 'rotate-180 text-[#E2231A]' : ''}`} />
                    </button>

                    {mktmMenuOpen && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white shadow-2xl rounded-b-xl rounded-tr-xl border border-slate-200 z-50 p-2 animate-in fade-in zoom-in-95 duration-100">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2.5 py-1">
                            Organizare &amp; Invitați MKTM
                          </span>
                          
                          <Link 
                            to={`/events/${eventId}/mktm`}
                            onClick={() => setMktmMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname.endsWith('/mktm') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <FileSpreadsheet className="w-3.5 h-3.5 text-red-600" />
                              <span>MKTM (Toți Invitații)</span>
                            </div>
                            {location.pathname.endsWith('/mktm') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>

                          <Link 
                            to={`/events/${eventId}/guests/staff`}
                            onClick={() => setMktmMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname.includes('/staff') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <Shield className="w-3.5 h-3.5 text-purple-600" />
                              <span>Staff</span>
                            </div>
                            {location.pathname.includes('/staff') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Guests Dropdown Menu (Desktop & Tablet) */}
                  <div className="relative flex-shrink-0 z-50" ref={guestsDropdownRef}>
                    <button 
                      type="button"
                      onClick={() => setGuestsMenuOpen(prev => !prev)}
                      className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 cursor-pointer ${
                        guestsMenuOpen || location.pathname.includes('/guests/')
                          ? 'bg-white text-black font-bold border-[#E2231A]' 
                          : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                      }`}
                      aria-expanded={guestsMenuOpen}
                    >
                      <Users className="mr-2 h-4 w-4 flex-shrink-0" />
                      Invitați
                      <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform ${guestsMenuOpen ? 'rotate-180 text-[#E2231A]' : ''}`} />
                    </button>

                    {guestsMenuOpen && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white shadow-2xl rounded-b-xl rounded-tr-xl border border-slate-200 z-50 p-2 animate-in fade-in zoom-in-95 duration-100">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2.5 py-1">
                            Categorii Invitați Eveniment
                          </span>
                          
                          <Link 
                            to={`/events/${eventId}/guests/lenovo-team`}
                            onClick={() => setGuestsMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname.includes('/guests/lenovo-team') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <Users className="w-3.5 h-3.5 text-red-600" />
                              <span>Lenovo Team</span>
                            </div>
                            {location.pathname.includes('/guests/lenovo-team') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>

                          <Link 
                            to={`/events/${eventId}/distributors`}
                            onClick={() => setGuestsMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname.includes('/distributors') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <Building2 className="w-3.5 h-3.5 text-blue-600" />
                              <span>Distribuitori</span>
                            </div>
                            {location.pathname.includes('/distributors') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>

                          <Link 
                            to={`/events/${eventId}/guests/resellers`}
                            onClick={() => setGuestsMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname.includes('/guests/resellers') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <Users className="w-3.5 h-3.5 text-blue-600" />
                              <span>Reselleri</span>
                            </div>
                            {location.pathname.includes('/guests/resellers') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>

                          <Link 
                            to={`/events/${eventId}/guests/end-users`}
                            onClick={() => setGuestsMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname.includes('/guests/end-users') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <Users className="w-3.5 h-3.5 text-emerald-600" />
                              <span>End Useri (Lenovo)</span>
                            </div>
                            {location.pathname.includes('/guests/end-users') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>

                          <Link 
                            to={`/events/${eventId}/guests/partner-end-users`}
                            onClick={() => setGuestsMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname.includes('/guests/partner-end-users') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <Users className="w-3.5 h-3.5 text-amber-600" />
                              <span>End Useri (Reselleri)</span>
                            </div>
                            {location.pathname.includes('/guests/partner-end-users') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>

                          <Link 
                            to={`/events/${eventId}/guests/managers`}
                            onClick={() => setGuestsMenuOpen(false)} 
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-colors border-t border-slate-100 ${location.pathname.includes('/guests/managers') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <div className="flex items-center gap-2">
                              <UserCheck className="w-3.5 h-3.5 text-purple-600" />
                              <span>Invitați pe Manageri</span>
                            </div>
                            {location.pathname.includes('/guests/managers') && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                            )}
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>

                  <Link
                    to={`/events/${eventId}/validations`}
                    className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 flex-shrink-0 ${
                      location.pathname.includes('/validations')
                        ? 'bg-white text-black font-bold border-[#E2231A]' 
                        : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                    }`}
                  >
                    <ShieldAlert className="mr-2 h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span>Validări</span>
                    {pendingPhonesCount > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-red-100 text-[#C41A12]">
                        {pendingPhonesCount}
                      </span>
                    )}
                  </Link>

                  <NavItem to={`/events/${eventId}/scanner`} icon={QrCode} label="Check-in" isActive={location.pathname.includes('/scanner')} />
                  {canAccessMarketing && (
                    <NavItem to={`/events/${eventId}/invitations`} icon={ImageIcon} label="Materiale Marketing" isActive={location.pathname.includes('/invitations')} />
                  )}
                  {canAccessSms && (
                    <NavItem to={`/events/${eventId}/sms`} icon={MessageSquare} label="Campanii SMS" isActive={location.pathname.includes('/sms')} />
                  )}
                </>
              )}
              {!isAdmin && (
                <NavItem to={`/events/${eventId}/partner`} icon={Users} label="Partner Portal" isActive={location.pathname.includes('/partner')} />
              )}
            </div>
            
            {/* Admin Menu Dropdown (Desktop & Tablet) */}
            {isAdmin && (
              <div className="flex-shrink-0 relative pl-2" ref={adminDropdownRef}>
                <button 
                  onClick={() => setMegaMenuOpen(!megaMenuOpen)}
                  className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 ${
                    megaMenuOpen || location.pathname.includes('/team') || location.pathname.includes('/mktm') || location.pathname.includes('/clients') || location.pathname.includes('/validations') || location.pathname.includes('/settings')
                      ? 'bg-white text-black font-bold border-[#E2231A]' 
                      : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                  }`}
                  aria-expanded={megaMenuOpen}
                >
                  Admin <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform ${megaMenuOpen ? 'rotate-180 text-[#E2231A]' : ''}`} />
                </button>

                {/* Submenu Dropdown Container - Protected against horizontal overflow */}
                {megaMenuOpen && (
                  <div className="absolute top-full right-0 mt-1 w-64 sm:w-72 max-w-[calc(100vw-1.5rem)] bg-white shadow-2xl rounded-b-xl rounded-tl-xl border border-slate-200 z-50 p-2.5 animate-in fade-in zoom-in-95 duration-100">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                        Setări & Administrare
                      </span>
                      
                      {user?.isSuperAdmin && (
                        <>
                          <Link 
                            to="/mktm-team" 
                            onClick={() => setMegaMenuOpen(false)} 
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname === '/mktm-team' || location.pathname === '/mktm' ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <Users className="w-3.5 h-3.5 text-[#E2231A]" />
                            Echipa MKTM (Hostess & Admini)
                          </Link>

                          <Link 
                            to="/team" 
                            onClick={() => setMegaMenuOpen(false)} 
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${location.pathname === '/team' ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-slate-50 hover:text-black'}`}
                          >
                            <Briefcase className="w-3.5 h-3.5 text-[#E2231A]" />
                            Echipa Lenovo
                          </Link>
                        </>
                      )}

                      {(user?.role === 'admin' || user?.isSuperAdmin) && (
                        <>
                          <div className="rounded-lg border border-slate-100 p-1 bg-slate-50/50">
                            <Link 
                              to="/clients" 
                              onClick={() => setMegaMenuOpen(false)} 
                              className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${location.pathname === '/clients' ? 'bg-red-50 text-[#C41A12]' : 'text-slate-700 hover:bg-white hover:text-black'}`}
                            >
                              <div className="flex items-center gap-2">
                                <Database className="w-3.5 h-3.5 text-[#E2231A]" />
                                <span>Baza Date Clienți</span>
                              </div>
                              {location.pathname === '/clients' && (
                                <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>
                              )}
                            </Link>
                            {effectiveEventId && (
                              <div className="pl-4 space-y-0.5 border-l-2 border-slate-200 ml-2.5 my-1">
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block px-2 py-0.5">
                                  Validări
                                </span>
                                <Link
                                  to={`/events/${effectiveEventId}/validations`}
                                  onClick={() => setMegaMenuOpen(false)}
                                  className={`flex items-center justify-between px-2 py-1 rounded text-[11px] font-medium transition-colors ${location.pathname.endsWith('/validations') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-white hover:text-black'}`}
                                >
                                  <div className="flex items-center gap-1.5">
                                    <ShieldAlert className="w-3 h-3 text-amber-600" />
                                    <span>Validări Înscrieri</span>
                                  </div>
                                  {location.pathname.endsWith('/validations') && <span className="w-1.5 h-1.5 rounded-full bg-[#E2231A]"></span>}
                                </Link>
                                <Link
                                  to={`/events/${effectiveEventId}/validations/phones`}
                                  onClick={() => setMegaMenuOpen(false)}
                                  className={`flex items-center justify-between px-2 py-1 rounded text-[11px] font-medium transition-colors ${location.pathname.includes('/validations/phones') ? 'bg-red-50 text-[#C41A12] font-bold' : 'text-slate-600 hover:bg-white hover:text-black'}`}
                                >
                                  <div className="flex items-center gap-1.5">
                                    <Smartphone className="w-3 h-3 text-[#E2231A]" />
                                    <span>Validări Telefoane</span>
                                  </div>
                                  {pendingPhonesCount > 0 && (
                                    <span className="px-1.5 py-0.2 bg-red-100 text-[#C41A12] text-[9px] font-bold rounded-full">
                                      {pendingPhonesCount}
                                    </span>
                                  )}
                                </Link>
                              </div>
                            )}
                          </div>
                          <div className="border-t border-slate-100 my-1 pt-1 space-y-1">
                            {user?.isSuperAdmin && (
                              <Link 
                                to={eventId ? `/events/${eventId}/settings` : "/settings"} 
                                onClick={() => setMegaMenuOpen(false)} 
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${location.pathname.includes('/settings') ? 'bg-red-50 text-[#C41A12]' : 'text-slate-800 hover:bg-slate-50'}`}
                              >
                                <Settings className="w-3.5 h-3.5 text-[#E2231A]" />
                                Setări Integrări (Mailgun & SMS)
                              </Link>
                            )}
                            <Link 
                              to={eventId ? `/events/${eventId}/help` : "/help"} 
                              onClick={() => setMegaMenuOpen(false)} 
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${location.pathname.includes('/help') || location.pathname.includes('/manual') ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'text-slate-800 hover:bg-slate-50'}`}
                            >
                              <BookOpen className="w-3.5 h-3.5 text-amber-600" />
                              Manual Utilizare & FAQ
                            </Link>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sub-navbar (Level 2) - Global Admin Pages */}
      {(hideSidebar || !eventId) && (user?.role === 'admin' || user?.isSuperAdmin) && (
        <div className="hidden lg:block bg-[#F2F2F2] border-b border-gray-300 shadow-xs sticky top-16 z-40">
          <div className="w-full max-w-[1920px] 2xl:max-w-full mx-auto px-2 sm:px-6 lg:px-8 flex justify-between items-center min-w-0">
            <div className="flex items-center flex-1 min-w-0 py-0.5 overflow-visible">
              <Link
                to="/clients"
                className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 flex-shrink-0 ${
                  location.pathname === '/clients'
                    ? 'bg-white text-black font-bold border-[#E2231A]'
                    : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                }`}
              >
                <Database className="mr-2 h-4 w-4 text-[#E2231A] flex-shrink-0" />
                Baza Date Clienți
              </Link>
              {user?.isSuperAdmin && (
                <>
                  <Link
                    to="/team"
                    className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 flex-shrink-0 ${
                      location.pathname === '/team'
                        ? 'bg-white text-black font-bold border-[#E2231A]'
                        : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                    }`}
                  >
                    <Briefcase className="mr-2 h-4 w-4 text-[#E2231A] flex-shrink-0" />
                    Echipa Lenovo
                  </Link>
                  <Link
                    to="/mktm-team"
                    className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 flex-shrink-0 ${
                      location.pathname === '/mktm-team' || location.pathname === '/mktm'
                        ? 'bg-white text-black font-bold border-[#E2231A]'
                        : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                    }`}
                  >
                    <Users className="mr-2 h-4 w-4 text-[#E2231A] flex-shrink-0" />
                    Echipa MKTM
                  </Link>
                  <Link
                    to="/settings"
                    className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 flex-shrink-0 ${
                      location.pathname === '/settings'
                        ? 'bg-white text-black font-bold border-[#E2231A]'
                        : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                    }`}
                  >
                    <Settings className="mr-2 h-4 w-4 text-[#E2231A] flex-shrink-0" />
                    Integrări
                  </Link>
                </>
              )}
              <Link
                to="/help"
                className={`flex items-center px-3.5 py-3 text-sm transition-colors whitespace-nowrap border-b-2 flex-shrink-0 ${
                  location.pathname === '/help' || location.pathname === '/manual'
                    ? 'bg-white text-black font-bold border-[#E2231A]'
                    : 'text-[#4A4A4A] border-transparent font-medium hover:bg-white/80 hover:text-black'
                }`}
              >
                <BookOpen className="mr-2 h-4 w-4 text-[#E2231A] flex-shrink-0" />
                Manual Utilizare
              </Link>
            </div>

            {effectiveEventId && (
              <div className="flex-shrink-0 pl-4 py-2">
                <Link
                  to={`/events/${effectiveEventId}/dashboard`}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-slate-700 hover:text-[#C41A12] bg-white border border-slate-200 hover:border-[#E2231A] rounded-lg shadow-2xs transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5 text-[#E2231A]" />
                  <span>Înapoi la Eveniment</span>
                  {event?.name && (
                    <span className="text-slate-400 font-normal max-w-[160px] truncate hidden xl:inline">
                      ({event.name})
                    </span>
                  )}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dynamic Contextual Breadcrumbs & Back Navigation */}
      <NavigationBreadcrumbs event={event} effectiveEventId={effectiveEventId} />

      {/* Main Content Area */}
      <main className="flex-1 w-full max-w-[1920px] 2xl:max-w-full mx-auto p-3 sm:p-5 lg:p-6 2xl:p-8">
        <Outlet />
      </main>
    </div>
  );
}

